import React, { useState, useEffect } from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { getServicesForCurrentUser } from '../../services/serviceService';
import { createServiceRequest } from '../../services/serviceRequestService';
import { Service } from '../../types/service';
import { uploadMultipleFiles } from '../../services/uploadService';
import { CreateServiceRequestData } from '../../types/serviceRequest';
import formStyles from '../../components/forms/Form.module.css'; // استخدام تنسيقات النموذج المشتركة
import { FaUpload } from 'react-icons/fa';
import pageStyles from '../Page.module.css';
import Icon from '../../components/common/Icon';

// (2) تحديث نوع بيانات النموذج
type FormInputs = {
  service_id: string;
  note_text: string;
  documents: FileList; // حقل الملفات
};

const NewServiceRequestPage: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [selectedService, setSelectedService] = useState<Service | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const [isUploading, setIsUploading] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<FormInputs>();

  // جلب الخدمات المتاحة عند تحميل الصفحة
  useEffect(() => {
    const fetchServices = async () => {
      try {
        setIsLoading(true);
        const availableServices = await getServicesForCurrentUser();
        setServices(availableServices);
      } catch (error) {
        toast.error('فشل في جلب الخدمات المتاحة.');
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    };
    fetchServices();
  }, []);

  // مراقبة اختيار الخدمة لتحديث الرسوم المعروضة
  const selectedServiceId = watch('service_id');
  useEffect(() => {
    const service = services.find(s => s.id === parseInt(selectedServiceId));
    setSelectedService(service || null);
  }, [selectedServiceId, services]);

  const onSubmit: SubmitHandler<FormInputs> = async (data) => {
    if (!selectedService) {
      toast.error('يرجى اختيار خدمة صالحة.');
      return;
    }

    let uploadedDocuments: { name: string; url: string }[] = [];

    // (4) !! خطوة رفع الملفات أولاً !!
    if (data.documents && data.documents.length > 0) {
      setIsUploading(true);
      try {
        const uploadedFiles = await uploadMultipleFiles(data.documents);
        // تحويل البيانات للشكل الذي نريده
        uploadedDocuments = uploadedFiles.map(file => ({
          name: file.originalname,
          url: file.url,
        }));
      } catch (error) {
        toast.error('فشل في رفع المستندات. يرجى المحاولة مرة أخرى.');
        setIsUploading(false);
        return; // إيقاف العملية إذا فشل الرفع
      } finally {
        setIsUploading(false);
      }
    }
    

     const requestData: CreateServiceRequestData = {
      service_id: selectedService.id,
      fee: selectedService.fee,
      note_text: data.note_text || undefined, // إرسال undefined إذا كان فارغًا
      documents: uploadedDocuments.length > 0 ? uploadedDocuments : undefined,
    };


    const promise = createServiceRequest(requestData);

    toast.promise(promise, {
      loading: 'جاري إرسال الطلب...',
      success: () => {
        navigate('/lawyer/dashboard'); // العودة إلى لوحة التحكم بعد النجاح
        return 'تم إرسال طلبك بنجاح!';
      },
      error: (err: any) => err.response?.data?.message || 'فشل في إرسال الطلب.',
    });
  };

  if (isLoading) {
    return <div className={formStyles.loading}>جاري تحميل الخدمات...</div>;
  }
  const totalLoading = isSubmitting || isUploading;

  return (
    <div className={formStyles.pageContainer}>
      <div className={formStyles.formContainer}>
        <h2 className={formStyles.title}>تقديم طلب خدمة جديد</h2>
        <form onSubmit={handleSubmit(onSubmit)} className={formStyles.form}>
          
          {/* اختيار الخدمة */}
          <div className={formStyles.formField}>
            <label htmlFor="service_id" className={formStyles.label}>اختر الخدمة المطلوبة</label>
            <select
              id="service_id"
              {...register('service_id', { required: 'يجب اختيار خدمة' })}
              className={formStyles.input}
            >
              <option value="">-- يرجى الاختيار --</option>
              {services.map(service => (
                <option key={service.id} value={service.id}>
                  {service.name}
                </option>
              ))}
            </select>
            {errors.service_id && <p className={formStyles.error}>{errors.service_id.message}</p>}
          </div>

          {/* عرض تفاصيل الخدمة المختارة */}
          {selectedService && (
            <div className={formStyles.serviceDetails}>
              <h4>تفاصيل الخدمة</h4>
              <p>{selectedService.description}</p>
              <div className={formStyles.feeDisplay}>
                <strong>رسوم الخدمة:</strong>
                <span>{selectedService.fee} ريال</span>
              </div>
            </div>
          )}

          {/* يمكنك إضافة حقل لرفع الملفات هنا إذا لزم الأمر */}
          <div className={formStyles.formField}>
            <label htmlFor="note_text" className={formStyles.label}>ملاحظات إضافية (اختياري)</label>
            <textarea
              id="note_text"
              {...register('note_text')}
              className={formStyles.textarea}
              rows={4}
              placeholder="أضف أي تفاصيل أو ملاحظات تراها ضرورية..."
            />
          </div>

          {/* (7) !! إضافة حقل رفع الملفات !! */}
          <div className={formStyles.formField}>
            <label htmlFor="documents" className={formStyles.label}>إرفاق مستندات (اختياري)</label>
            <div className={formStyles.fileInputContainer}>
              <input
                type="file"
                id="documents"
                {...register('documents')}
                multiple // للسماح باختيار عدة ملفات
                className={formStyles.fileInput}
              />
              <label htmlFor="documents" className={formStyles.fileInputLabel}>
                 <Icon as={FaUpload} style={pageStyles.icon } />
                <span>اختر الملفات...</span>
              </label>
            </div>
          </div>


           <button type="submit" disabled={totalLoading || !selectedService} className={formStyles.submitButton}>
            {isUploading ? 'جاري رفع الملفات...' : isSubmitting ? 'جاري الإرسال...' : 'تأكيد وإرسال الطلب'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default NewServiceRequestPage;
